import {Component, computed, CUSTOM_ELEMENTS_SCHEMA, HostListener, inject} from '@angular/core';
import {Router, RouterOutlet} from '@angular/router';
import {AppNavigationLinkComponent} from '../../components/app-navigation-link/app-navigation-link.component';
import {
    AppNavigationLinkSubitemComponent
} from '../../components/app-navigation-link-subitem/app-navigation-link-subitem.component';
import {NavBarComponent} from '../../components/nav-bar/nav-bar.component';
import {AuthService} from '../../security/auth.service';
import {PortfolioSegmentDataContext} from '../../data/portfolio-segment-data-context.service';
import {ApiConfiguration} from '../../restclient/api.config';
import {AppDevToolsService} from '../../service/app-devtools.service';
import {Alerts} from '../../lib/alerts';
import {AppVersion} from '../../app.version';
import {DatePipe} from '@angular/common';
import {PortfolioDataContext} from '../../data/portfolio-data-context.service';
import {AccessControlDirective} from '../../directives/access-control.directive';
import {AppConstants} from '../../app.constants';
import {PortfolioPeriodEnum} from '../../../api-client-kt/src';
import {WjGridUtils} from '../../lib/wjgrid-utils';
import {StringUtils} from '../../lib/strings';
import {AccessControlService} from '../../security/access-control.service';
import {WijmoFormatPipe} from '../../pipes/wijmo-format.pipe';
import {AngularUtils} from '../../lib/angular';

@Component({
    selector: 'app-main-layout',
    standalone: true,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        RouterOutlet,
        AppNavigationLinkComponent,
        AppNavigationLinkSubitemComponent,
        NavBarComponent,
        DatePipe,
        AccessControlDirective,
        WijmoFormatPipe
    ],
    templateUrl: './main-layout.component.html',
    styleUrl: './main-layout.component.scss',
    providers:  [
        PortfolioDataContext, PortfolioSegmentDataContext
    ]
})
export class MainLayoutComponent{

    protected readonly AppVersion = AppVersion;

    protected readonly authService = inject(AuthService)
    protected readonly pfData = inject(PortfolioDataContext);
    protected readonly pfSegData = inject(PortfolioSegmentDataContext);

    private readonly router = inject(Router);
    private readonly accessControlSvc = inject(AccessControlService);

    protected readonly devToolsSvc = inject(AppDevToolsService);
    protected readonly devToolsRoleBadgeDisplay = computed(() => {
        return !!this.devToolsSvc.rolePermissions() ? 'block' : 'none';
    });

    protected readonly isCtxPfActive = computed(() => !PortfolioDataContext.isNullPortfolioId(this.pfSegData.portfolioId()));
    protected readonly isCtxMemberActive = computed(() => this.pfSegData.activeSegment.value() != null);

    protected readonly period = this.pfSegData.periodFilter;

    protected readonly PortfolioPeriodEnum = PortfolioPeriodEnum;
    protected readonly ApiConfiguration = ApiConfiguration;

    protected readonly selectedCellValue = WjGridUtils.selectedCellUnformattedValue;
    protected readonly selectedRangeSummary = WjGridUtils.selectedRangeSummary;

    protected readonly isSaveDisabled = computed(() => {
        const canEditSegmentDataOrAccountData = this.accessControlSvc.canEditSegmentCtx(this.pfSegData).allowed
            || this.accessControlSvc.canEditSomeAccount(this.pfSegData.activeSegment.value()!, this.pfSegData.portfolio.value()!).allowed;
            return !canEditSegmentDataOrAccountData || !this.pfSegData.isDirty();
        }
    );

    constructor() {
    }

    //@HostListener('window:beforeunload', ['$event'])
    protected handleBeforeUnload(event: Event) {
        if (this.pfSegData.isDirty()) {
            Alerts.alertPopup({ "body": "You have unsaved data changes. Are you sure to close the page?" });
            event.preventDefault();
            return undefined;
        } else {
            return undefined;
        }
    }

    @HostListener('window:keydown', ['$event'])
    protected async handleSpecialKeys(event: KeyboardEvent) {
        if (event.key === 'u' && event.ctrlKey && event.altKey) {
            this.pfSegData.checkAccountUpdatesInBackground();
        }
    }

    protected breadcrumbs(): string {
        const portfolio = this.pfData.portfolio.value();
        const segment = this.pfSegData.activeSegment.value();
        const steps = [
            portfolio?.uwyear?.toString(),
            portfolio?.phase,
            portfolio?.period,
            segment?.lobName,
            segment?.officeName
        ];
        return steps.filter(it => it != null).join(' > ');
    }

    protected dirtyIcon(): string {
        if (this.pfSegData.isDirty()){
            return 'fa-asterisk';
        } else {
            return '';
        }
    }

    protected portfolioLink(path: string): string {
        const portfolioId = this.pfSegData.portfolioId() ?? AppConstants.NULL_UUID;
        return `/app/pfm/${portfolioId}/${path}`;
    }

    protected segmentLink(path: string): string {
        const portfolioId = this.pfSegData.portfolioId();
        const segmentId = this.pfSegData.segmentId();
        path = path.replace(/^\//, '');

        if (segmentId) {
            return `/app/pfm/${portfolioId}/segment/${segmentId}/${path}`;
        } else if (portfolioId) {
            return `/app/pfm/${portfolioId}/${path}`;
        } else {
            return `/app/${path}`;
        }
    }

    async onShowUserDetails() {
        await this.router.navigateByUrl('/app/userdetails');
    }

    protected async onUserLogin() {
        await this.authService.login();
    }

    protected async onUserLogout() {
        await this.authService.logout();
    }

    async onShowDevToolsRolePermissions() {
        const rolePermissions = this.devToolsSvc.rolePermissions();
        await Alerts.alertPopup({ body: JSON.stringify(rolePermissions) });
    }

    async onExitPortfolio() {
        await this.pfSegData.deactivatePortfolioAndSegment();
        await this.pfData.deactivatePortfolio();
        await this.router.navigateByUrl('/app/archive');
    }

    onRecalculate() {
        this.pfSegData.updateFormulasAndValidations();
    }

    protected togglePeriod(period: PortfolioPeriodEnum) {
        this.pfSegData.togglePeriodFilter(period);
    }

    protected isPeriodActive(period: PortfolioPeriodEnum) : boolean | undefined {
        return AngularUtils.trueOrUndefined(this.period() === period);
    }

    protected onCopySelectedValueToClipboard() {
        const value = this.selectedRangeSummary().sumNumeric;
        if (value != null) {
            navigator.clipboard.writeText(value.toString());
        }
    }

    protected readonly StringUtils = StringUtils;

    protected async saveSegment() {
        const canEditSegment = this.accessControlSvc.canEditSegment(this.pfSegData.activeSegment.value()!, this.pfSegData.portfolio.value()!).allowed;
        const saveWithSegmentParameters = canEditSegment;
        console.debug("Saving, including segment parameters:", saveWithSegmentParameters);
        await this.pfSegData.save(saveWithSegmentParameters)
    }
}
